<script setup lang="ts">
import type { ComponentInternalInstance } from "vue";
import type { CultureDto, DropdownOption } from "../types";
import {
  useCurrentCulture,
  useHeadlessCulturesResource,
  computed,
  useRequestURL,
  useRoute,
  useRequestProtocol, ref, getCurrentInstance, watch,
} from "#imports";

type CultureOption = DropdownOption & CultureDto;
type FilterValue = "internal" | "external" | "auto" | "off";

export type SiteSwitcherConfig = {
  /**
   * A flag to indicate if the site switcher should redirect to the home page
   * cultures, opposed to the current page cultures
   */
  redirectToHome: boolean;
  /**
   * The key for the query parameter that controls the language switcher visibility
   */
  enableByQueryParamKey?: string;

  /**
   * The value of the query parameter that will enable the language switcher
   */
  enableByQueryParamValue?: string;
};

const props = withDefaults( defineProps<{
  cultureNameMaps?: Record<string, string>;
  filter?: null | FilterValue;
  config: SiteSwitcherConfig;
}>(), {
  cultureNameMaps: undefined,
  filter: "auto",
  config: undefined,
} );

const requestUrl = useRequestURL();
const route = useRoute();
const currentCulture = useCurrentCulture();
const cultureResource = useHeadlessCulturesResource();

const currentCultureRoutes = ref<ContentCultureRoutesDto | undefined>( undefined );
const cultures = ref<CultureDto[] | undefined>( undefined );

// handles re-rendering of the site switcher
const siteSwitcherInstance: ComponentInternalInstance | null = getCurrentInstance();
if ( !siteSwitcherInstance?.vnode.key ) {
  /*
     It’s important to use the key attribute for the site switcher to enable Vue’s virtual DOM
     to efficiently track and manage re-renders. This helps ensure that the necessary components
     is updated when the route changes, improving performance, in comparison to the watcher below.
   */
  console.warn( "To ensure efficient re-rendering of the site switcher, please implement the following dynamic key attribute as shown: <site-switcher :key='route.path' />" ); // eslint-disable-line no-console
  /*
    Using a watcher increases the amount of work Vue has to do in the virtual DOM, resulting in
    slower rendering and visual disruptions, such as "page jumping"
   */
  const loadCultureRoutes = async ( path: string ) => {
    const currentCultureRoutesResponse = await cultureResource.listCultureRoutesByRoute( path );
    const culturesResponse = await cultureResource.listCultures();

    currentCultureRoutes.value = currentCultureRoutesResponse.data.value;
    cultures.value = culturesResponse.data.value;
  };

  watch(
    () => route.fullPath,
    async ( path ) => {
      await loadCultureRoutes( props.config?.redirectToHome ? "/" : path );
    },
    {
      immediate: true,
    },
  );
} else {
  const currentCultureRoutesResponse = await cultureResource.listCultureRoutesByRoute( props.config?.redirectToHome ? "/" : route.path );
  const culturesResponse = await cultureResource.listCultures();

  currentCultureRoutes.value = currentCultureRoutesResponse.data.value;
  cultures.value = culturesResponse.data.value;
}

const cultureRoutesMap = computed<Record<string, string>>( () => currentCultureRoutes.value?.routesMap || {} );

const formattedQuery = computed<string>( () => {
  const queryString = new URLSearchParams(
    Object.entries( route.query ).reduce<Record<string, string>>( ( acc, [key, value] ) => {
      acc[key] = Array.isArray( value ) ? value.join( "," ) : String( value );
      return acc;
    }, {} ),
  ).toString();

  return queryString ? `?${queryString}` : "";
} );

const normalisedCultureNameMap = computed<Record<string, string>>( () => {
  if ( !props.cultureNameMaps ) {
    return {};
  }

  const original = props.cultureNameMaps;
  const clone: Record<string, string> = {};

  Object.keys( original )
    .forEach( ( key ) => {
      const lowercasedKey = key.toLowerCase();
      clone[lowercasedKey] = original[key];
    } );

  return clone;
} );

const cultureOptions = computed<CultureOption[]>( () => {
  const createCultureOption = ( cultureDto: CultureDto ): CultureOption => {
    const { culture } = cultureDto;

    const cultureRouteKey = findCultureRouteKey( cultureRoutesMap.value, culture );
    const cultureRoute = cultureRoutesMap.value[cultureRouteKey];

    const baseUrl = getLocalizedOrigin( cultureRoute );
    const routeHash = determineRouteHash( baseUrl, route.hash );
    const href = generateHref( baseUrl, formattedQuery.value, routeHash );
    const label = mapCultureName( cultureDto );

    return {
      culture,
      href,
      label,
    } as CultureOption;
  };

  const cultureOptions: CultureOption[] = Object.keys( cultureRoutesMap.value )
    .map( ( cultureKey ) => {
      const cultureDto = cultures.value?.find( ( { culture } ) =>
        culture.toLowerCase() === cultureKey.toLowerCase(),
      );
      return cultureDto ? createCultureOption( cultureDto ) : null;
    } )
    .filter( Boolean )
    .sort( ( a, b ) => a!.label.localeCompare( b!.label ) ) as CultureOption[];

  // handle filtering if applicable
  if ( props.filter && props.filter !== "off" ) {
    const internalDomain = "edcowebsites";
    const filter = props.filter === "auto"
      ? ( requestUrl.hostname.includes( internalDomain ) ? "internal" : "external" )
      : props.filter;

    const isExternal = filter === "external";
    return cultureOptions.filter( option => isExternal
      ? !option.href?.includes( internalDomain )
      : option.href?.includes( internalDomain ),
    );
  }

  return cultureOptions;
} );

const alternativeCultures = computed<CultureOption[]>( () => cultureOptions.value.filter( x => x.culture !== currentCulture.value ) );

const currentCultureOption = computed<CultureOption | undefined>( () => cultureOptions.value.find( x => x.culture.toLowerCase() === currentCulture.value?.toLowerCase() ) );

// update the site's culture state
watch( () => currentCultureOption,
  ( option ) => {
    if ( option.value?.culture ) {
      currentCulture.value = option.value.culture;
    }
  } );

function findCultureRouteKey ( routesMap: Record<string, unknown>, culture: string ): string {
  return Object.keys( routesMap ).find( key => key.toLowerCase() === culture.toLowerCase() ) || "";
}

function determineRouteHash ( baseUrl: string, routeHash: string ): string {
  return baseUrl && routeHash === "#translation-not-found" ? "" : routeHash;
}

function generateHref ( baseUrl: string, query: string, routeHash: string ): string {
  return baseUrl ? `${baseUrl}${query}${routeHash}` : "#translation-not-found";
}

function mapCultureName ( { name, culture }: CultureDto ): string {
  if ( normalisedCultureNameMap.value[culture] ) {
    return normalisedCultureNameMap.value[culture];
  }
  return name || culture;
}

function getLocalizedOrigin ( url: string ): string {
  const scheme = useRequestProtocol();
  let baseUrl: string;

  if ( url.startsWith( "/" ) ) {
    baseUrl = requestUrl.origin + url; // i.e.. /en, /de, /it, /fr etc.
  } else if ( !url.startsWith( "http" ) ) {
    baseUrl = `${scheme}://${url}`; // // i.e.. www.example.com/en, www.example.com/de, www.example.com/it, www.example.com/fr etc.
  } else {
    baseUrl = url; // assume host is complete
  }

  return baseUrl.replace( /\/+$/, "" );
}

const hasOptions = computed( () => !!alternativeCultures.value.length );

const displaySiteSwitcher = computed( () => {
  let result = alternativeCultures.value.length > 0;

  // check if there is an enable by query parameter value specified within the config
  if ( props.config?.enableByQueryParamKey ) {
    const queryValue = route.query[props.config.enableByQueryParamKey];
    result = queryValue === props.config.enableByQueryParamValue;
  }

  return result;
} );
</script>

<template>
  <div
    v-show="displaySiteSwitcher"
    :key="route.fullPath">
    <div
      v-if="hasOptions"
      class="site-switcher">
      <a-dropdown :options="alternativeCultures">
        <slot :current="currentCultureOption">
          {{ currentCultureOption?.label || "Unknown culture" }}
        </slot>

        <template #item="{ item }">
          <slot
            name="item"
            :item="item">
            {{ item.label || "Unknown culture" }}
          </slot>
        </template>

        <span class="sr-only">
          Switch site - {{ currentCultureOption?.label ?? "Unknown culture" }} is currently selected
        </span>
      </a-dropdown>
    </div>
    <div
      v-else
      class="site-switcher dropdown__button">
      <slot
        name="no-option"
        :current-culture="currentCulture" />
    </div>
  </div>
</template>
